import { ChatProjectMenu } from '@/components/chat/ChatProjectMenu';
import { DraggableChatRow } from '@/components/chat/DraggableChatRow';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useDroppable } from '@dnd-kit/core';
import type { ChatProject, ChatSession } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { Folder, FolderOpen } from 'lucide-react';
import { useState } from 'react';

interface Props {
  sessions: ChatSession[];
  activePath: string;
  renamingId: string | null;
  onStartRename: (id: string) => void;
  onDoneRename: () => void;
  onEditProject: (project: ChatProject) => void;
}

/**
 * 사이드바의 프로젝트 영역.
 * - 프로젝트별로 collapsible 트리. 펼침 상태는 컴포넌트 로컬 state.
 * - 각 프로젝트 헤더 = drop zone (드래그 중인 채팅을 받는다).
 * - 펼친 상태일 때 그 프로젝트에 속한 채팅들이 indent 되어 렌더.
 */
export function ChatProjectsSection({
  sessions,
  activePath,
  renamingId,
  onStartRename,
  onDoneRename,
  onEditProject,
}: Props) {
  const projectsQuery = useQuery({
    queryKey: ['chat', 'projects'],
    queryFn: api.chatProjectList,
    staleTime: 5_000,
  });

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const projects = projectsQuery.data?.projects ?? [];

  if (projects.length === 0) {
    return (
      <div className="px-2.5 py-1 text-[11px] text-foreground-faint">아직 프로젝트가 없습니다</div>
    );
  }

  // 프로젝트별 채팅 분배. 즐겨찾기여도 프로젝트에 들어있으면 여기서 보임 — 다만
  // Layout 의 분류 로직에서 즐겨찾기는 별도 그룹으로 빠지므로 일반적으로는
  // !isFavorite & projectId 매치 만 들어온다.
  const byProject = new Map<string, ChatSession[]>();
  for (const s of sessions) {
    if (!s.projectId) continue;
    const arr = byProject.get(s.projectId) ?? [];
    arr.push(s);
    byProject.set(s.projectId, arr);
  }

  return (
    <ul className="space-y-0.5">
      {projects.map((p) => {
        const open = expanded[p.id] ?? false;
        const children = byProject.get(p.id) ?? [];
        return (
          <ProjectNode
            key={p.id}
            project={p}
            open={open}
            onToggle={() => setExpanded((m) => ({ ...m, [p.id]: !open }))}
            onEdit={() => onEditProject(p)}
            childSessions={children}
            activePath={activePath}
            renamingId={renamingId}
            onStartRename={onStartRename}
            onDoneRename={onDoneRename}
          />
        );
      })}
    </ul>
  );
}

interface ProjectNodeProps {
  project: ChatProject;
  open: boolean;
  onToggle: () => void;
  onEdit: () => void;
  childSessions: ChatSession[];
  activePath: string;
  renamingId: string | null;
  onStartRename: (id: string) => void;
  onDoneRename: () => void;
}

function ProjectNode({
  project,
  open,
  onToggle,
  onEdit,
  childSessions,
  activePath,
  renamingId,
  onStartRename,
  onDoneRename,
}: ProjectNodeProps) {
  const { setNodeRef, isOver } = useDroppable({
    id: `project:${project.id}`,
    data: { type: 'project', projectId: project.id },
  });

  const Icon = open ? FolderOpen : Folder;

  return (
    <li className="group">
      {/* relative 는 헤더 div 에 둠 — ⋯ 메뉴의 absolute 기준점이 헤더가 되어, 폴더가 펼쳐져
          자식 채팅들을 포함해 li 가 길어져도 메뉴가 헤더 우측에 고정. */}
      <div
        ref={setNodeRef}
        className={cn(
          'relative flex items-center gap-2.5 h-9 pl-2.5 pr-8 rounded-md text-sm transition-colors',
          isOver
            ? 'bg-accent/15 text-foreground ring-1 ring-accent'
            : 'text-foreground-muted hover:bg-surface-muted hover:text-foreground',
        )}
      >
        <Icon className="size-4 shrink-0" />
        <button
          type="button"
          onClick={onToggle}
          className="flex-1 min-w-0 text-left truncate"
          title={project.description ?? undefined}
        >
          {project.name}
        </button>
        {project.sessionCount > 0 ? (
          <span className="text-[10px] text-foreground-faint shrink-0">{project.sessionCount}</span>
        ) : null}
        <div className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
          <ChatProjectMenu project={project} onEdit={onEdit} />
        </div>
      </div>
      {open && childSessions.length > 0 ? (
        <ul className="mt-0.5 space-y-0.5">
          {childSessions.map((s) => (
            <DraggableChatRow
              key={s.id}
              session={s}
              activePath={activePath}
              renamingId={renamingId}
              onStartRename={onStartRename}
              onDoneRename={onDoneRename}
              indent
            />
          ))}
        </ul>
      ) : null}
      {open && childSessions.length === 0 ? (
        <div className="pl-7 py-1 text-[11px] text-foreground-faint">비어있음</div>
      ) : null}
    </li>
  );
}
